<script setup>
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import Avatar from 'next/avatar/Avatar.vue';
import ConversationContextMenu from './contextMenu/Index.vue';
import MessagePreview from './MessagePreview.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import { dynamicTime, shortTimestamp } from 'shared/helpers/timeHelper';
import { useI18n } from 'vue-i18n';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import VoiceCallStatus from './VoiceCallStatus.vue';
import { useChannelIcon, useChannelColor } from 'next/icon/provider';
import { getContrastingTextColor } from '@chatwoot/utils';

/* eslint-disable vue/no-unused-properties -- hideInboxName e showAssignee
   fazem parte do contrato do componente: ChatList, ConversationItem e
   ContactConversations os passam. Remover a declaracao os jogaria em attrs. */
const props = defineProps({
  activeLabel: { type: String, default: '' },
  chat: { type: Object, default: () => ({}) },
  hideInboxName: { type: Boolean, default: false },
  hideThumbnail: { type: Boolean, default: false },
  teamId: { type: [String, Number], default: 0 },
  foldersId: { type: [String, Number], default: 0 },
  showAssignee: { type: Boolean, default: false },
  conversationType: { type: String, default: '' },
  selected: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
  enableContextMenu: { type: Boolean, default: false },
  allowedContextMenuOptions: { type: Array, default: () => [] },
});

const emit = defineEmits([
  'contextMenuToggle',
  'assignAgent',
  'assignLabel',
  'assignTeam',
  'markAsUnread',
  'markAsRead',
  'assignPriority',
  'updateConversationStatus',
  'deleteConversation',
  'selectConversation',
  'deSelectConversation',
]);

const router = useRouter();
const store = useStore();
const { t } = useI18n();

const hovered = ref(false);
const showContextMenu = ref(false);
const contextMenu = ref({
  x: null,
  y: null,
});

const currentChat = useMapGetter('getSelectedChat');
const activeInbox = useMapGetter('getSelectedInbox');
const accountId = useMapGetter('getCurrentAccountId');

const chatMetadata = computed(() => props.chat.meta || {});

const assignee = computed(() => chatMetadata.value.assignee || {});

// Estado na fila de atribuição (calculado no backend): queued | limbo | none
const queueState = computed(() => chatMetadata.value.queue_state);

const senderId = computed(() => chatMetadata.value.sender?.id);

const currentContact = computed(() => {
  return senderId.value
    ? store.getters['contacts/getContact'](senderId.value)
    : {};
});

const isGroup = computed(() => props.chat.is_group === true);
const groupName = computed(
  () => props.chat.additional_attributes?.group_name || ''
);
const groupAvatar = computed(
  () => props.chat.additional_attributes?.group_avatar || ''
);
const displayName = computed(() =>
  isGroup.value && groupName.value ? groupName.value : currentContact.value.name
);

const isActiveChat = computed(() => {
  return currentChat.value.id === props.chat.id;
});

const unreadCount = computed(() => props.chat.unread_count);

const hasUnread = computed(() => unreadCount.value > 0);

const voiceCallData = computed(() => ({
  status: props.chat.additional_attributes?.call_status,
  direction: props.chat.additional_attributes?.call_direction,
}));

const inboxId = computed(() => props.chat.inbox_id);

const inbox = computed(() => {
  return inboxId.value ? store.getters['inboxes/getInbox'](inboxId.value) : {};
});

const inboxIcon = useChannelIcon(inbox);
const inboxColor = useChannelColor(inbox);

// A prioridade saiu daqui (virou moldura do avatar), entao a faixa de meta so
// existe se houver nome de caixa — senao sobrava uma linha vazia.
const PRIORITY_RINGS = {
  urgent: 'ring-n-ruby-9',
  high: 'ring-n-amber-9',
  medium: 'ring-n-blue-9',
  low: 'ring-n-slate-8',
};
const priorityRingClass = computed(() => {
  const cor = PRIORITY_RINGS[props.chat?.priority];
  return cor ? `ring-2 ring-inset ${cor}` : '';
});

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);

// Um tempo so: desde a ultima mensagem. O "1y • 5d" exigia decifrar qual
// numero era o que.
const encurtar = ts => {
  const curto = shortTimestamp(dynamicTime(ts));
  return curto === 'now' ? t('CHAT_LIST.TIME_NOW') : curto;
};
const tempoCurto = computed(() =>
  props.chat.timestamp ? encurtar(props.chat.timestamp) : ''
);
const tempoEspera = computed(() =>
  props.chat.waiting_since ? encurtar(props.chat.waiting_since) : ''
);

// Previa da ultima mensagem: a informacao n.1 para decidir sem abrir.
const ultimaMensagem = computed(() => {
  const msgs = props.chat.messages || [];
  for (let i = msgs.length - 1; i >= 0; i -= 1) {
    if (msgs[i].message_type !== 2) return msgs[i];
  }
  return props.chat.last_non_activity_message || null;
});

// Chip de estado: quem espera (ambar) fala mais alto que quem esta servido.
// Sem responsavel: na fila (com tempo) ou limbo. Com responsavel: so quando
// o cliente aguarda resposta.
// Cantinho de posse: foto do agente, bolinha ambar (fila) ou cinza (limbo).
// O nome escrito e o "esperando" por extenso roubavam a linha inteira.
const posse = computed(() => {
  if (assignee.value.name) return 'agente';
  // bot na frente: a Clara responde ate alguem assumir
  if (props.chat.bot_active) return 'bot';
  return queueState.value === 'queued' ? 'fila' : 'limbo';
});
const tooltipPosse = computed(() => {
  if (posse.value === 'agente') return assignee.value.name;
  if (posse.value === 'bot') return t('CHAT_LIST.BOT_ATTENDING');
  if (posse.value === 'fila') {
    const extra = tempoEspera.value ? ` · ${tempoEspera.value}` : '';
    return `${t('CHAT_LIST.QUEUE_STATE.QUEUED')}${extra}`;
  }
  return t('CHAT_LIST.QUEUE_STATE.NO_QUEUE');
});

// Etiquetas sao protagonistas: chip com cor + nome (2 + contador)
const accountLabels = useMapGetter('labels/getLabels');
const etiquetasCard = computed(() => {
  const titulos = props.chat.labels || [];
  if (!titulos.length) return [];
  const porTitulo = new Map(
    (accountLabels.value || []).map(l => [l.title, l.color])
  );
  return titulos.slice(0, 2).map(titulo => {
    const cor = porTitulo.get(titulo) || '#94A3B8';
    return { titulo, cor, texto: getContrastingTextColor(cor) };
  });
});
const etiquetasExtras = computed(() =>
  Math.max(0, (props.chat.labels || []).length - 2)
);

const conversationPath = computed(() => {
  return frontendURL(
    conversationUrl({
      accountId: accountId.value,
      activeInbox: activeInbox.value,
      id: props.chat.id,
      label: props.activeLabel,
      teamId: props.teamId,
      conversationType: props.conversationType,
      foldersId: props.foldersId,
    })
  );
});

const onCardClick = e => {
  const path = conversationPath.value;
  if (!path) return;

  // Handle Ctrl/Cmd + Click for new tab
  if (e.metaKey || e.ctrlKey) {
    e.preventDefault();
    window.open(
      `${window.chatwootConfig.hostURL}${path}`,
      '_blank',
      'noopener,noreferrer'
    );
    return;
  }

  // Skip if already active
  if (isActiveChat.value) return;

  router.push({ path });
};

const onThumbnailHover = () => {
  hovered.value = !props.hideThumbnail;
};

const onThumbnailLeave = () => {
  hovered.value = false;
};

const onSelectConversation = checked => {
  if (checked) {
    emit('selectConversation', props.chat.id, inbox.value.id);
  } else {
    emit('deSelectConversation', props.chat.id, inbox.value.id);
  }
};

const openContextMenu = e => {
  if (!props.enableContextMenu) return;
  e.preventDefault();
  emit('contextMenuToggle', true);
  contextMenu.value.x = e.pageX || e.clientX;
  contextMenu.value.y = e.pageY || e.clientY;
  showContextMenu.value = true;
};

const closeContextMenu = () => {
  emit('contextMenuToggle', false);
  showContextMenu.value = false;
  contextMenu.value.x = null;
  contextMenu.value.y = null;
};

const onUpdateConversation = (status, snoozedUntil) => {
  closeContextMenu();
  emit('updateConversationStatus', props.chat.id, status, snoozedUntil);
};

const onAssignAgent = agent => {
  emit('assignAgent', agent, [props.chat.id]);
  closeContextMenu();
};

const onAssignLabel = label => {
  emit('assignLabel', [label.title], [props.chat.id]);
  closeContextMenu();
};

const onAssignTeam = team => {
  emit('assignTeam', team, props.chat.id);
  closeContextMenu();
};

const markAsUnread = () => {
  emit('markAsUnread', props.chat.id);
  closeContextMenu();
};

const markAsRead = () => {
  emit('markAsRead', props.chat.id);
  closeContextMenu();
};

const assignPriority = priority => {
  emit('assignPriority', priority, props.chat.id);
  closeContextMenu();
};

const deleteConversation = () => {
  emit('deleteConversation', props.chat.id);
  closeContextMenu();
};
</script>

<template>
  <div
    class="relative flex items-start flex-grow-0 flex-shrink-0 w-auto max-w-full gap-[10px] cursor-pointer conversation group rounded-xl mx-1.5 transition-colors duration-100 bg-white dark:bg-[#2A2522] border border-n-weak dark:border-[#3B3530] shadow-[0_1px_2px_rgba(28,25,23,0.04)]"
    :class="{
      active: isActiveChat,
      'ring-1 ring-n-slate-6': selected && !isActiveChat,
      'px-0 py-0': compact,
      'px-3 py-2': !compact,
    }"
    @click="onCardClick"
    @contextmenu="openContextMenu($event)"
  >
    <!-- Prioridade como MOLDURA do avatar: as barrinhas ficavam acima do nome,
         disputando espaco com o nome da caixa, e exigiam contar quantas
         estavam acesas. A borda colorida le-se de relance na coluna que o
         olho ja percorre. Sem prioridade, sem moldura. -->
    <div
      class="relative flex items-center justify-center rounded-full shrink-0 size-[42px]"
      :class="priorityRingClass"
      @mouseenter="onThumbnailHover"
      @mouseleave="onThumbnailLeave"
    >
      <!-- selo do canal no canto: libera a linha de texto que o nome da
           caixa ocupava -->
      <span
        v-if="inboxIcon && !hideThumbnail"
        class="absolute -bottom-0.5 -right-0.5 z-10 grid size-4 place-content-center rounded-full bg-white shadow ring-1 ring-n-weak dark:bg-n-solid-3"
      >
        <span class="size-3" :class="[inboxIcon, inboxColor]" />
      </span>
      <Avatar
        v-if="isGroup && groupAvatar && !hideThumbnail"
        :name="groupName"
        :src="groupAvatar"
        :size="34"
        rounded-full
      />
      <div
        v-else-if="isGroup && !hideThumbnail"
        class="flex items-center justify-center size-[34px] rounded-full bg-n-teal-3 shrink-0"
      >
        <span class="i-lucide-users size-4 text-n-teal-11" />
      </div>
      <Avatar
        v-else-if="!hideThumbnail"
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="34"
        :status="currentContact.availability_status"
        hide-offline-status
        rounded-full
      >
        <template #overlay="{ size }">
          <label
            v-if="hovered || selected"
            class="flex items-center justify-center rounded-full cursor-pointer absolute inset-0 z-10 backdrop-blur-[2px]"
            :style="{ width: `${size}px`, height: `${size}px` }"
            @click.stop
          >
            <input
              :value="selected"
              :checked="selected"
              class="!m-0 cursor-pointer"
              type="checkbox"
              @change="onSelectConversation($event.target.checked)"
            />
          </label>
        </template>
      </Avatar>
    </div>
    <div class="flex-1 min-w-0">
      <!-- linha 1: nome + tempo unico -->
      <div class="flex min-w-0 items-baseline gap-2">
        <h4
          class="conversation--user my-0 mx-0 min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-[13px] capitalize text-n-stone-800"
          :class="hasUnread ? 'font-bold' : 'font-medium'"
        >
          <span
            v-if="isGroup"
            class="i-lucide-users size-3.5 text-n-slate-9 inline-block align-text-bottom mr-0.5"
          />{{ displayName }}
        </h4>
        <span
          class="flex flex-shrink-0 items-center gap-1 text-[10.5px] tabular-nums text-n-slate-9"
        >
          <span
            v-if="chat.additional_attributes?.has_brain_insight"
            v-tooltip="$t('CAPTAIN.BRAIN.HAS_INSIGHT')"
            class="i-lucide-sparkles size-3 text-iris-7"
          />
          {{ tempoCurto }}
        </span>
      </div>
      <VoiceCallStatus
        v-if="voiceCallData.status"
        key="voice-status-row"
        :status="voiceCallData.status"
        :direction="voiceCallData.direction"
      />
      <!-- linha 2: previa da ultima mensagem + nao lidas -->
      <div class="mt-0.5 flex min-w-0 items-center gap-1.5">
        <MessagePreview
          v-if="ultimaMensagem"
          :message="ultimaMensagem"
          class="min-w-0 flex-1 text-[12px] leading-4"
          :class="hasUnread ? 'text-n-slate-12' : 'text-n-slate-10'"
        />
        <p v-else class="my-0 h-4 min-w-0 flex-1" />
        <span
          v-if="hasUnread"
          class="min-w-[17px] flex-shrink-0 rounded-full bg-n-brand px-1 text-center text-[9.5px] font-bold leading-[17px] text-white"
        >
          {{ unreadCount > 9 ? '9+' : unreadCount }}
        </span>
      </div>
      <!-- linha 3: etiquetas a esquerda; no canto, espera minima + caixa
           (aba Todos) + posse: foto do agente / bolinha de fila / limbo -->
      <div class="mt-1.5 flex min-w-0 items-center gap-1">
        <SLACardLabel v-if="hasSlaPolicyId" :chat="chat" class="shrink-0" />
        <span
          v-for="etiqueta in etiquetasCard"
          :key="etiqueta.titulo"
          class="inline-flex min-w-0 items-center truncate rounded px-1.5 py-px text-[9.5px] font-bold"
          :style="{ backgroundColor: etiqueta.cor, color: etiqueta.texto }"
        >
          <span class="truncate">{{ etiqueta.titulo }}</span>
        </span>
        <span
          v-if="etiquetasExtras"
          class="shrink-0 rounded-md border border-n-weak bg-n-alpha-1 px-1.5 py-px text-[10px] font-bold text-n-slate-9"
        >
          +{{ etiquetasExtras }}
        </span>
        <span class="ml-auto flex flex-shrink-0 items-center gap-1.5 pl-1">
          <span
            v-if="showAssignee && inbox.name"
            class="inline-flex max-w-[110px] items-center truncate rounded-md border border-n-weak bg-n-alpha-1 px-1.5 py-px text-[10px] text-n-slate-10"
          >
            <span class="truncate">{{ inbox.name }}</span>
          </span>
          <span
            v-if="posse === 'agente'"
            v-tooltip="tooltipPosse"
            class="inline-flex rounded-full ring-1 ring-n-weak"
          >
            <Avatar
              :name="assignee.name"
              :src="assignee.thumbnail"
              :size="18"
              rounded-full
            />
          </span>
          <span
            v-else-if="posse === 'bot'"
            v-tooltip="tooltipPosse"
            class="grid size-[18px] place-content-center rounded-full bg-n-slate-3 ring-1 ring-n-slate-6"
          >
            <span class="i-ph-sparkle-fill size-2.5 text-n-brand" />
          </span>
          <span
            v-else-if="posse === 'fila'"
            v-tooltip="tooltipPosse"
            class="posse-fila size-2.5 rounded-full bg-n-amber-9"
          />
          <span
            v-else
            v-tooltip="tooltipPosse"
            class="size-2.5 rounded-full bg-n-slate-6"
          />
        </span>
      </div>
    </div>
    <ContextMenu
      v-if="showContextMenu"
      :x="contextMenu.x"
      :y="contextMenu.y"
      @close="closeContextMenu"
    >
      <ConversationContextMenu
        :status="chat.status"
        :inbox-id="inbox.id"
        :priority="chat.priority"
        :chat-id="chat.id"
        :has-unread-messages="hasUnread"
        :conversation-url="conversationPath"
        :allowed-options="allowedContextMenuOptions"
        @update-conversation="onUpdateConversation"
        @assign-agent="onAssignAgent"
        @assign-label="onAssignLabel"
        @assign-team="onAssignTeam"
        @mark-as-unread="markAsUnread"
        @mark-as-read="markAsRead"
        @assign-priority="assignPriority"
        @delete-conversation="deleteConversation"
        @close="closeContextMenu"
      />
    </ContextMenu>
  </div>
</template>

<style scoped>
/* Ativo e hover falam pela BORDA: o fundo do card agora e sempre branco
   sobre a coluna crua, entao mudar o fundo apagava o card inteiro. */
.conversation.active {
  border-color: rgb(var(--n-brand)) !important;
  box-shadow: 0 0 0 1px rgb(var(--n-brand)) inset;
}

.conversation:not(.active):hover {
  border-color: rgb(var(--n-slate-6)) !important;
}

/* fila pulsa de leve: chama o olho sem gritar */
.posse-fila {
  animation: pulso-fila 2s ease-in-out infinite;
}

@keyframes pulso-fila {
  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.45;
  }
}
</style>
